import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { InfinitySpin } from "react-loader-spinner";
import Wrapper from "../../Components/Wrapper";
import { useToast } from "../../Components/Toast/ToastContext";
import { ToastType } from "../../Components/Toast/Toast";
import { Modal } from "../../Components/Modal";
import { APIUtility } from "../../Networking/ApiUtility";
import { SunsetAPIAccountContext } from "../../Networking/Interface/Account/AccountAPIProvider";
import { BugReport, CharacterDTO } from "../../Networking/Interface/Account/AccountInterface";
import styles from "./BugPanel.module.css";

type FilterOption = "open" | "resolved" | "all";
type SortKey = "id" | "userId" | "status" | "description";
type SortDirection = "asc" | "desc";

const formatDevNote = (note?: string | null) => {
  if (!note) {
    return "—";
  }

  const trimmed = note.replace(/^"+|"+$/g, "").trim();
  return trimmed.length ? trimmed : "—";
};

const BugReportPage: React.FC = () => {
  const accountApi = useContext(SunsetAPIAccountContext);
  const { showToast } = useToast();

  const [bugReports, setBugReports] = useState<BugReport[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterOption>("open");
  const [searchTerm, setSearchTerm] = useState("");
  const [updatingBugId, setUpdatingBugId] = useState<number | null>(null);
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortKey, setSortKey] = useState<SortKey>("id");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const [showRewardModal, setShowRewardModal] = useState(false);
  const [rewardBug, setRewardBug] = useState<BugReport | null>(null);
  const [characters, setCharacters] = useState<CharacterDTO[]>([]);
  const [selectedCharacterId, setSelectedCharacterId] = useState<number | null>(null);
  const [rewardAmount, setRewardAmount] = useState<string>("");

  const [showEditModal, setShowEditModal] = useState(false);
  const [editBug, setEditBug] = useState<BugReport | null>(null);
  const [editDevNote, setEditDevNote] = useState<string>("");

  const fetchBugReports = useCallback(async () => {
    if (!accountApi) {
      setError("Account API unavailable.");
      setLoading(false);
      setBugReports([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await accountApi.getBugs();
      if (!response.success || !response.data) {
        throw new Error(response.error || "Failed to load bug reports.");
      }

      setBugReports(response.data);
      setCurrentPage(1);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load bug reports.";
      setError(message);
      setBugReports([]);
    } finally {
      setLoading(false);
    }
  }, [accountApi]);

  const fetchCharacters = useCallback(async (accountId: number) => {
    if (!accountApi) return;

    try {
      const response = await accountApi.getCharactersByAccountID(accountId);
      if (response.success && response.data) {
        setCharacters(response.data);
        if (response.data.length > 0) {
          setSelectedCharacterId(response.data[0].id);
        }
      }
    } catch (err) {
      showToast(ToastType.Error, "Failed to load characters", 5);
    }
  }, [accountApi, showToast]);

  useEffect(() => {
    fetchBugReports();
  }, [fetchBugReports]);

  const counts = useMemo(() => {
    return bugReports.reduce(
      (acc, report) => {
        if (report.isSolved === 0) {
          acc.open += 1;
        } else {
          acc.resolved += 1;
        }
        return acc;
      },
      { open: 0, resolved: 0 },
    );
  }, [bugReports]);

  const normalizedSearch = searchTerm.trim().toLowerCase();

  const filteredReports = useMemo(() => {
    return bugReports.filter((report) => {
      if (filter === "open" && report.isSolved !== 0) {
        return false;
      }

      if (filter === "resolved" && report.isSolved === 0) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const haystack = `${report.description || ""} ${report.devNote || ""} ${report.userId} ${report.id}`.toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [bugReports, filter, normalizedSearch]);

  const sortedReports = useMemo(() => {
    const sorted = [...filteredReports];
    sorted.sort((a, b) => {
      let aValue: number | string = 0;
      let bValue: number | string = 0;

      switch (sortKey) {
        case "userId":
          aValue = a.userId;
          bValue = b.userId;
          break;
        case "status":
          aValue = a.isSolved;
          bValue = b.isSolved;
          break;
        case "description":
          aValue = a.description?.toLowerCase() ?? "";
          bValue = b.description?.toLowerCase() ?? "";
          break;
        case "id":
        default:
          aValue = a.id;
          bValue = b.id;
          break;
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortDirection === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      const numericA = Number(aValue);
      const numericB = Number(bValue);
      return sortDirection === "asc" ? numericA - numericB : numericB - numericA;
    });

    return sorted;
  }, [filteredReports, sortDirection, sortKey]);

  const totalPages = Math.max(1, Math.ceil(sortedReports.length / pageSize));
  const currentPageSafe = Math.min(currentPage, totalPages);
  const paginatedReports = useMemo(() => {
    const startIndex = (currentPageSafe - 1) * pageSize;
    return sortedReports.slice(startIndex, startIndex + pageSize);
  }, [currentPageSafe, pageSize, sortedReports]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchTerm, sortKey, sortDirection, pageSize]);

  const handleSolveBug = async (bugId: number) => {
    if (!accountApi) {
      return;
    }

    setUpdatingBugId(bugId);
    try {
      const response = await accountApi.solveBugReport(bugId);
      if (!response.success) {
        throw new Error(response.error || "Unable to solve bug report.");
      }

      setBugReports((prev) =>
        prev.map((report) =>
          report.id === bugId ? { ...report, isSolved: 1 } : report,
        ),
      );
      showToast(ToastType.Success, "Bug report marked as solved", 4);

      // Find the solved report
      const solvedReport = bugReports.find(r => r.id === bugId);
      if (solvedReport) {
        setRewardBug(solvedReport);
        setShowRewardModal(true);
        fetchCharacters(solvedReport.userId);
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Unable to solve bug report.";
      showToast(ToastType.Error, message, 5);
    } finally {
      setUpdatingBugId(null);
    }
  };

  const handleReward = async () => {
    if (!accountApi) {
      showToast(ToastType.Error, "Account API unavailable", 5);
      return;
    }

    if (!selectedCharacterId || !rewardAmount) {
      showToast(ToastType.Error, "Please select a character and enter an amount", 5);
      return;
    }

    try {
      const savedUnauthorizedHandler = APIUtility.onUnauthorized;
      APIUtility.onUnauthorized = null;

      let response;
      try {
        response = await accountApi.rewardPlayer(selectedCharacterId, parseInt(rewardAmount));
      } finally {
        APIUtility.onUnauthorized = savedUnauthorizedHandler;
      }

      if (response.success) {
        showToast(ToastType.Success, "Reward sent successfully", 4);
        setShowRewardModal(false);
        setRewardBug(null);
        setCharacters([]);
        setSelectedCharacterId(null);
        setRewardAmount("");
      } else {
        throw new Error(response.error || "Failed to send reward");
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to send reward";
      showToast(ToastType.Error, message, 5);
    }
  };

  const handleEditDevNote = async () => {
    if (!accountApi || !editBug) {
      showToast(ToastType.Error, "Account API unavailable", 5);
      return;
    }

    try {
      const response = await accountApi.updateBugReport(editBug.id, editDevNote);
      if (response.success) {
        setBugReports((prev) =>
          prev.map((report) =>
            report.id === editBug.id ? { ...report, devNote: editDevNote } : report,
          ),
        );
        showToast(ToastType.Success, "Dev note updated successfully", 4);
        setShowEditModal(false);
        setEditBug(null);
        setEditDevNote("");
      } else {
        throw new Error(response.error || "Failed to update dev note");
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to update dev note";
      showToast(ToastType.Error, message, 5);
    }
  };

  const renderBody = () => {
    if (loading) {
      return (
        <div className={styles.loader}>
          <InfinitySpin width="180" color="#e7752e" />
        </div>
      );
    }

    if (error) {
      return <div className={styles.errorBanner}>{error}</div>;
    }

    if (sortedReports.length === 0) {
      return (
        <div className={styles.emptyState}>
          <p>No bug reports match the current filters.</p>
          <button
            type="button"
            className={styles.refreshButton}
            onClick={fetchBugReports}
          >
            Refresh data
          </button>
        </div>
      );
    }

    return (
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>ID</th>
              <th>Reporter</th>
              <th>Description</th>
              <th>Developer Note</th>
              <th>Status</th>
              <th>Solved By</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedReports.map((report) => {
              const isOpen = report.isSolved === 0;
              return (
                <tr key={report.id}>
                  <td>#{report.id}</td>
                  <td>
                    <span className={styles.reporterId}>{report.name ? `${report.name} (${report.userId})` : `User #${report.userId}`}</span>
                  </td>
                  <td className={styles.descriptionCell}>{report.description}</td>
                  <td className={styles.devNoteCell}>{formatDevNote(report.devNote)}</td>
                  <td>
                    <span
                      className={`${styles.statusBadge} ${
                        isOpen ? styles.statusOpen : styles.statusResolved
                      }`}
                    >
                      {isOpen ? "Open" : "Resolved"}
                    </span>
                  </td>
                  <td>
                    {(report.solvedBy ?? report.solved_by) != null
                      ? <span className={styles.reporterId}>Account #{report.solvedBy ?? report.solved_by}</span>
                      : <span className={styles.resolvedCopy}>{isOpen ? "—" : "Unknown"}</span>
                    }
                  </td>
                  <td>
                    {isOpen ? (
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          type="button"
                          className={styles.resolveButton}
                          onClick={() => handleSolveBug(report.id)}
                          disabled={updatingBugId === report.id}
                        >
                          {updatingBugId === report.id
                            ? "Saving..."
                            : "Mark solved"}
                        </button>
                        <button
                          type="button"
                          className={styles.editButton}
                          onClick={() => {
                            setEditBug(report);
                            setEditDevNote(report.devNote || "");
                            setShowEditModal(true);
                          }}
                        >
                          Edit Note
                        </button>
                      </div>
                    ) : (
                      <span className={styles.resolvedCopy}>All good</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const filters: { key: FilterOption; label: string; count: number }[] = [
    { key: "open", label: "Open", count: counts.open },
    { key: "resolved", label: "Resolved", count: counts.resolved },
    { key: "all", label: "All", count: bugReports.length },
  ];

  return (
    <Wrapper width="1200px">
      <section className={styles.page}>
        <header className={styles.header}>
          <div>
            <p className={styles.eyebrow}>Staff tools</p>
            <h1 className={styles.title}>Bug Reports</h1>
            <p className={styles.subtitle}>
              Track the latest player-reported issues and mark them as solved
              when a fix is deployed.
            </p>
          </div>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.refreshButton}
              onClick={fetchBugReports}
              disabled={loading}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </header>

        <div className={styles.filters}>
          <div className={styles.filterButtons}>
            {filters.map((option) => (
              <button
                key={option.key}
                type="button"
                className={`${styles.filterButton} ${
                  filter === option.key ? styles.filterButtonActive : ""
                }`}
                onClick={() => setFilter(option.key)}
              >
                {option.label}
                <span className={styles.countBubble}>{option.count}</span>
              </button>
            ))}
          </div>
          <input
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            className={styles.searchInput}
            placeholder="Search description, note, or ID"
          />
        </div>

        <div className={styles.controlsRow}>
          <div className={styles.sortControls}>
            <label className={styles.controlLabel}>
              Sort by
              <select
                value={sortKey}
                onChange={(event) => setSortKey(event.target.value as SortKey)}
                className={styles.select}
              >
                <option value="id">Bug ID</option>
                <option value="userId">Reporter ID</option>
                <option value="status">Status</option>
                <option value="description">Description</option>
              </select>
            </label>
            <label className={styles.controlLabel}>
              Direction
              <select
                value={sortDirection}
                onChange={(event) =>
                  setSortDirection(event.target.value as SortDirection)
                }
                className={styles.select}
              >
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </select>
            </label>
          </div>
          <label className={styles.controlLabel}>
            Rows per page
            <select
              value={pageSize}
              onChange={(event) => setPageSize(Number(event.target.value))}
              className={styles.select}
            >
              {[10, 25, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
        </div>

        {renderBody()}

        <footer className={styles.pagination}>
          <div className={styles.paginationInfo}>
            Showing
            <strong>
              {sortedReports.length === 0
                ? 0
                : (currentPageSafe - 1) * pageSize + 1}
            </strong>
            to
            <strong>
              {Math.min(currentPageSafe * pageSize, sortedReports.length)}
            </strong>
            of
            <strong>{sortedReports.length}</strong>
            reports
          </div>
          <div className={styles.paginationControls}>
            <button
              type="button"
              className={styles.pageButton}
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPageSafe === 1}
            >
              Prev
            </button>
            <span className={styles.pageIndicator}>
              Page {currentPageSafe} / {totalPages}
            </span>
            <button
              type="button"
              className={styles.pageButton}
              onClick={() =>
                setCurrentPage((prev) => Math.min(totalPages, prev + 1))
              }
              disabled={currentPageSafe === totalPages}
            >
              Next
            </button>
          </div>
        </footer>
      </section>

      {showRewardModal && rewardBug && (
        <Modal onClose={() => setShowRewardModal(false)}>
          <div className={styles.modalContent}>
            <h2>Reward Player for Bug Report #{rewardBug.id}</h2>
            <p>Reward the player for reporting this bug.</p>
            <div className={styles.formGroup}>
              <label>
                Character:
                <select
                  value={selectedCharacterId || ""}
                  onChange={(e) => setSelectedCharacterId(parseInt(e.target.value))}
                  className={styles.select}
                >
                  {characters.map((char) => (
                    <option key={char.id} value={char.id}>
                      {(char as any).character_name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className={styles.formGroup}>
              <label>
                Amount:
                <input
                  type="number"
                  value={rewardAmount}
                  onChange={(e) => setRewardAmount(e.target.value)}
                  placeholder="Enter amount"
                  className={styles.input}
                />
              </label>
            </div>
            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={() => setShowRewardModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.rewardButton}
                onClick={handleReward}
              >
                Send Reward
              </button>
            </div>
          </div>
        </Modal>
      )}

      {showEditModal && editBug && (
        <Modal onClose={() => setShowEditModal(false)}>
          <div className={styles.modalContent}>
            <h2>Edit Dev Note for Bug Report #{editBug.id}</h2>
            <p>Update the developer note for this bug report.</p>
            <div className={styles.formGroup}>
              <label>
                Dev Note:
                <textarea
                  value={editDevNote}
                  onChange={(e) => setEditDevNote(e.target.value)}
                  placeholder="Enter dev note"
                  className={styles.textarea}
                  rows={4}
                />
              </label>
            </div>
            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.saveButton}
                onClick={handleEditDevNote}
              >
                Save
              </button>
            </div>
          </div>
        </Modal>
      )}
    </Wrapper>
  );
};

export default BugReportPage;
